import React, { useEffect, useReducer } from 'react';
import { Route, Redirect, useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';

import { TRoute } from '../infra/types';
import { podeAcessarRota } from '../components/AppShell/navItems';
import { RenovadorDeSessao } from '../components/RenovadorDeSessao';
import { SplashScreen } from '../components/SplashScreen';

import {
  FalhaTemporariaError,
  encerrarSessao,
  performLogout,
  renovarSessao,
  usuarioDaSessao,
} from 'src/services/session';

const AppShell = React.lazy(async () => import('../components/AppShell'));

const S_TO_MS_FACTOR = 1000;

const AuthRoute: React.FC<TRoute> = ({ component: Component, ...props }: TRoute) => {
  const history = useHistory();
  const [, reler] = useReducer((versao: number) => versao + 1, 0);
  const logged = usuarioDaSessao();
  const token = Cookies.get('token') as string;
  const refreshToken = Cookies.get('refreshToken');
  const tokenExpirationTimeInSeconds = Number(Cookies.get('tokenExpiration')) || 0;

  const isAuth =
    !!token &&
    !!logged &&
    tokenExpirationTimeInSeconds * S_TO_MS_FACTOR > Number(new Date().getTime());

  const isSessionExpired =
    (!!token || !!logged) &&
    tokenExpirationTimeInSeconds * S_TO_MS_FACTOR < Number(new Date().getTime());

  // Token vencido (ou cookie já apagado) com refresh token: renova antes de decidir.
  const precisaRenovar = !isAuth && !!logged && !!refreshToken;

  useEffect(() => {
    if (!precisaRenovar && !isSessionExpired) return undefined;

    let ativo = true;

    function irParaOLogin() {
      history.replace({ pathname: props.redirect, state: { from: history.location } });
    }

    if (!precisaRenovar) {
      handleLogOut();
      return undefined;
    }

    renovarSessao().then(
      () => ativo && reler(),
      (erro) => {
        if (!ativo) return;

        // Servidor fora não apaga a sessão: a próxima rota autenticada tenta de novo.
        if (erro instanceof FalhaTemporariaError) {
          irParaOLogin();
          return;
        }

        encerrarSessao(history);
      }
    );

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precisaRenovar, isSessionExpired]);

  async function handleLogOut() {
    await performLogout();
    history.go(0);
  }

  return (
    <Route
      path={props.path}
      exact={props.exact}
      render={({ location }) => {
        const redirectDaRota = (
          <Redirect to={{ pathname: props.redirect, state: { from: location } }} />
        );

        if (!props.auth) {
          return isAuth ? redirectDaRota : <Component />;
        }

        if (precisaRenovar) {
          return <SplashScreen />;
        }

        if (!isAuth) {
          return redirectDaRota;
        }

        localStorage.removeItem('on-premisses/dashboards-permissions');

        // Logado, mas sem nível para a tela: volta para o início, não para o login.
        if (!podeAcessarRota(props.path, logged?.tipoUsuario, logged?.designerCliente)) {
          return <Redirect to={{ pathname: '/', state: { from: location } }} />;
        }

        // Modo TV do viewer: o shell continua montado e só deixa de desenhar topo
        // e lateral. Trocar o elemento aqui remontaria o viewer a cada entrada e
        // saída, e ele buscaria o modelo de novo.
        const semMoldura =
          props.path === '/' && new URLSearchParams(location.search).get('tv') === '1';

        return (
          <>
            <RenovadorDeSessao />
            <AppShell semMoldura={semMoldura}>
              <Component />
            </AppShell>
          </>
        );
      }}
    />
  );
};

export default AuthRoute;
